import React, { useState } from 'react'
import { Badge, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from 'reactstrap'
import { useQuery } from '@apollo/client'
import gql from 'graphql-tag'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import DatetimeRenderer from '../../Material/DatetimeRenderer'
import toTitleCase from '../../Functions/toTitleCase'
import SubrogationClaimInfo from './SubrogationClaimInfo'
import LiabilityClaimInfo from './LiabilityClaimInfo'
import AddSubrogationClaimModal from '../IncidentModals/AddSubrogationClaimModal'
import AddLiabilityClaimModal from '../IncidentModals/AddLiabilityClaimModal'
import HistoryList from '../../Material/HistoryList'
import ScopedDocumentListContainer from '../DocumentsTab/ScopedDocumentListContainer'
import ConfirmationPopup from '../../Material/ConfirmationPopup'
import { HasPermissionsQuery } from '../../Functions/querys'
import withApolloClient from "../../withApolloClient"
import './ClaimsTab.css'

const AllSubrogationClaims = gql`
  query AllSubrogationClaims($incidentId: ID!, $isArchived: Boolean) {
    allSubrogationClaims(incidentId: $incidentId, isArchived: $isArchived, orderBy: ["-date_added"]) {
      edges {
        node {
          id
          pk
          dateAdded
          documentSlug
          party { id partyName }
          status
          tpa { id name }
          tpaFee
          tpaExpenses
          adjuster {
            id
            name
            phone
            email
            role
          }
          filedDate
          acknowledgmentDate
          externalId
          paymentId
          paymentDate
          inLitigation
          actualRepair
          estimatedPd
          estimatedRepair
          estimatedLou
          estimatedDv
          estimatedTs
          recoveredPd
          recoveredLou
          recoveredDv
          recoveredTs
          dailyRate
          daysDown
          notes
          lossOfUse
          grossCollected
          requestedAmount
          tpaNetCollected
          netCollected
          recoveryRate
          liabilityPercentage
          isArchived
          metadata
          assignedTo {
            edges {
              node {
                id
                username
                firstName
                lastName
              }
            }
          }
        }
      }
    }
  }
`

const AllLiabilityClaims = gql`
  query AllLiabilityClaims($incidentId: ID!, $isArchived: Boolean) {
    allLiabilityClaims(incidentId: $incidentId, isArchived: $isArchived, orderBy: ["-date_added"]) {
        edges {
            node {
                id
                pk
                dateAdded
                externalId
                status
                documentSlug
                tpa { id name }
                filedDate
                acknowledgmentDate
                reservedAmount
                paidAmount
                inLitigation
                notes
                isArchived
                estimatedAmount
                claimAmount
                metadata
                adjuster{
                    id
                    name
                    phone
                    email
                    role
                }
            }
        }
    }
}`

const Incident = gql`
  query Incident($incidentId: ID!) {
    incident(incidentId: $incidentId) {
      id
      incidentDate
    }
  }
`

const UPDATE_LIABILITY_CLAIM = gql`
  mutation updateLiabilityClaim($input: UpdateLiabilityClaimMutationInput!) {
    updateLiabilityClaim(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const UPDATE_SUBROGATION_CLAIM = gql`
  mutation updateSubrogationClaim($input: UpdateSubrogationClaimMutationInput!) {
    updateSubrogationClaim(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const ClaimsTab = ({ id, apolloClient }) => {
    const [isArchived, setIsArchived] = useState(false)
    const { data: permissionsData } = useQuery(HasPermissionsQuery, {
        variables: { userPermissions: ["add_subrogationclaims", "add_liabilityclaim", "delete_liabilityclaim", "delete_subrogationclaim", "custom_can_add_multiple_claims"] },
    })

    const hasPermissions = permissionsData?.hasPermissions || []

    const { data: claimsData, loading: claimsLoading, refetch: claimsRefetch } = useQuery(AllSubrogationClaims, {
        variables: { incidentId: id, isArchived },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    })

    const { data: liabilityData, loading: liabilityLoading, refetch: liabilityRefetch } = useQuery(AllLiabilityClaims, {
        variables: { incidentId: id, isArchived },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    })

    const { data: incidentData, loading: incidentLoading } = useQuery(Incident, { variables: { incidentId: id } })

    const [openClaimId, setOpenClaimId] = useState(null)
    const [openLiabilityClaimId, setOpenLiabilityClaimId] = useState(null)
    const [openModal, setOpenModal] = useState(null)
    const [addClaimDropdownOpen, setAddClaimDropdownOpen] = useState(false)
    const [activeLiabilityTab, setActiveLiabilityTab] = useState({})
    const [activeSubroTab, setActiveSubroTab] = useState({})
    const [archiveModal, setArchiveModal] = useState(null)
    const [archiveLoading, setArchiveLoading] = useState(false)
    const [archiveError, setArchiveError] = useState("")

    const claims = claimsData?.allSubrogationClaims?.edges?.map(edge => edge.node) || []
    const liabilityClaims = liabilityData?.allLiabilityClaims?.edges?.map(edge => edge.node) || []
    const totalClaims = claims.length + liabilityClaims.length
    const isLoading = liabilityLoading || claimsLoading

    const handleClaimArchive = async () => {
        if (!archiveModal) return
        const { claimType, claimId, isArchived: archivedState } = archiveModal
        const mutation = claimType === 'liability' ? UPDATE_LIABILITY_CLAIM : UPDATE_SUBROGATION_CLAIM
        const dataKey = claimType === 'liability' ? 'updateLiabilityClaim' : 'updateSubrogationClaim'
        setArchiveLoading(true)
        setArchiveError("")
        try {
            const result = await apolloClient.mutate({
                mutation,
                variables: {
                    input: {
                        claimId,
                        isArchived: !archivedState
                    }
                }
            })
            const res = result?.data?.[dataKey]
            if (res?.ok) {
                if (claimType === 'liability') {
                    await liabilityRefetch()
                } else {
                    await claimsRefetch()
                }
                setArchiveModal(null)
            } else {
                setArchiveError(res?.errors?.[0]?.messages?.[0] || "Something went wrong, Please contact admin for support!")
            }
        } catch (error) {
            const errorMessage = "An error has occured" + (error.message ? (": " + error.message) : ".")
            setArchiveError(errorMessage)
        } finally {
            setArchiveLoading(false)
        }
    }

    const renderClaimRow = ({ claim, claimType }) => {
        const isLiability = claimType === 'liability'
        const isOpen = isLiability ? openLiabilityClaimId === claim.id : openClaimId === claim.id
        const activeTabState = isLiability ? activeLiabilityTab : activeSubroTab
        const setActiveTabState = isLiability ? setActiveLiabilityTab : setActiveSubroTab
        const tabKey = `${claimType}Tab_${claim.id}`
        const activeTab = activeTabState[tabKey] || 'details'

        const setOpen = () => {
            if (isLiability) {
                setOpenLiabilityClaimId(isOpen ? null : claim.id)
            } else {
                setOpenClaimId(isOpen ? null : claim.id)
            }
        }

        const setTab = (tab) => {
            setActiveTabState((prev) => ({ ...prev, [tabKey]: tab }))
        }

        return (
            <div key={claim.id} className="claims-row">
                <div className="claims-row__header" onClick={setOpen}>
                    <div className="claims-row__chevron">
                        <i className={`fa ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                    </div>
                    <div className="claims-row__info">
                        <div className="claims-row__title">
                            <span title='VGM ID'>#{claim.pk} - </span>
                            <span title={isLiability ? "Claim No" : "Party Name"}>
                                {isLiability ? claim.externalId ? claim.externalId : 'No Claim No' : claim?.party?.partyName || "No Party Selected"}
                            </span>
                            <span className="claim-row__default" title='Third party representative'>
                                {toTitleCase(claim?.tpa?.name) || '--'}
                            </span>
                        </div>
                        <div className="claims-row__subtitle">Added {claim.dateAdded ? <DatetimeRenderer datetime={claim.dateAdded} /> : '--'}</div>
                    </div>
                    <Badge className="claims-row__status--badges text-uppercase">{toTitleCase(claim.status) || '--'}</Badge>
                    <div className="claims-row__actions">
                        <button
                            type="button"
                            id={`archive-btn-${claim.pk}`}
                            disabled={!hasPermissions?.includes(claimType === 'liability' ? "delete_liabilityclaim" : "delete_subrogationclaim")}
                            className={`claims-row__action ${claim.isArchived ? 'unarchive' : 'archive'}`}
                            title={claim.isArchived ? "Un-Archive Claim" : "Archive Claim"}
                            onClick={(event) => {
                                event.stopPropagation()
                                setArchiveError("")
                                setArchiveModal({
                                    claimId: claim.id,
                                    claimType,
                                    isArchived: claim.isArchived
                                })
                            }}
                        >
                            {claim.isArchived ? <i className="fa fa-undo" /> : <i className="fa fa-trash" />}
                        </button>
                        {!hasPermissions?.includes(claimType === 'liability' ? "delete_liabilityclaim" : "delete_subrogationclaim") &&
                            <UncontrolledTooltip target={`archive-btn-${claim.pk}`} placement="top">
                                {`You don't have permission to ${claim.isArchived ? "un-archive" : "archive"} this claim`}
                            </UncontrolledTooltip>
                        }

                    </div>
                </div>

                {isOpen && (
                    <div className="claims-row__body">
                        <div className='mt-0'>
                            <div className='claims-panel__tabs mb-3'>
                                <button type="button" className={`claims-panel__tab ${activeTab === 'details' ? 'is-active' : ''}`} onClick={() => setTab('details')}>
                                    <i className="fa fa-info-circle" /> Details
                                </button>
                                <button type="button" className={`claims-panel__tab ${activeTab === 'documents' ? 'is-active' : ''}`} onClick={() => setTab('documents')}>
                                    <i className="fa fa-file-text-o" /> Documents
                                </button>
                                <button type="button" className={`claims-panel__tab ${activeTab === 'history' ? 'is-active' : ''}`} onClick={() => setTab('history')}>
                                    <i className="fa fa-history" /> History
                                </button>
                            </div>
                            {activeTab === 'details' && (
                                isLiability
                                    ? <LiabilityClaimInfo claim={claim} refetch={liabilityRefetch} incidentId={id} />
                                    : <SubrogationClaimInfo claim={claim} refetch={claimsRefetch} incidentId={id} />
                            )}
                            {activeTab === 'documents' && (
                                <ScopedDocumentListContainer
                                    id={id}
                                    slug={claim?.documentSlug}
                                    category={isLiability ? "LIABILITY" : "SUBROGATION"}
                                    viewType={isLiability ? "liability" : "subrogation"}
                                />
                            )}
                            {activeTab === 'history' && (
                                <HistoryList
                                    objectIds={claim?.id ? [claim.id] : []}
                                    emptyText="No history available"
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="overview-tab-container claims-management__content">
            <div className="claims-management__header">
                <div>
                    <div className="incident-overview-header__title">Claims Management</div>
                    <div className="incident-overview-header__subtitle">Manage all claims associated with the incident</div>
                </div>
                <div className="claims-management__toggle">
                    <strong>Show Archived</strong>
                    <i
                        className={`fa fa-toggle-${!isArchived ? "off" : "on"} archive-toggle-icon ${!isArchived ? "toggle-red" : "toggle-green"}`}
                        onClick={() => setIsArchived(!isArchived)}
                    />
                </div>
            </div>
            {archiveModal && (
                <ConfirmationPopup
                    open={true}
                    message={`Are you sure you want to ${archiveModal.isArchived ? "un-archive" : "archive"} this ${archiveModal.claimType} claim?`}
                    confirmAction={handleClaimArchive}
                    action={archiveModal.isArchived ? "Un-Archive" : "Yes"}
                    color={archiveModal.isArchived ? "success" : "danger"}
                    loading={archiveLoading}
                    handleClose={() => {
                        setArchiveModal(null)
                        setArchiveError("")
                    }}
                    error={archiveError}
                />
            )}
            {openModal === "subrogation" &&
                <AddSubrogationClaimModal
                    isOpen={openModal === 'subrogation'}
                    toggle={() => setOpenModal(null)}
                    incidentId={id}
                    onSuccess={claimsRefetch}
                />
            }
            {openModal === "liability" &&
                <AddLiabilityClaimModal
                    isOpen={openModal === 'liability'}
                    toggle={() => setOpenModal(null)}
                    incidentId={id}
                    onSuccess={liabilityRefetch}
                />
            }

            <div className="claims-toolbar">
                <div className="claims-toolbar__left">
                    <div className="claims-toolbar__title">All Claims ({totalClaims})</div>
                    <Badge className="claims-toolbar__badge claims-toolbar__badge--blue">
                        <i className="fa fa-balance-scale" /> {liabilityClaims.length} Liability
                    </Badge>
                    <Badge className="claims-toolbar__badge claims-toolbar__badge--teal">
                        <i className="fa fa-file-text-o" /> {claims.length} Subrogation
                    </Badge>
                </div>
                {!isArchived && (
                    <div className="claims-toolbar__right">
                        <Dropdown isOpen={addClaimDropdownOpen} toggle={() => setAddClaimDropdownOpen(!addClaimDropdownOpen)}>
                            <DropdownToggle caret color="primary" className="claims-toolbar__add">Add Claim</DropdownToggle>
                            <DropdownMenu end>
                                <DropdownItem onClick={() => { setOpenModal('liability'); setAddClaimDropdownOpen(false) }} disabled={!hasPermissions?.includes("add_liabilityclaim") || (!incidentLoading && !incidentData?.incident?.incidentDate) || (liabilityClaims.length > 0 && !hasPermissions?.includes("custom_can_add_multiple_claims"))}>
                                    Add Liability Claim
                                </DropdownItem>
                                <DropdownItem onClick={() => { setOpenModal('subrogation'); setAddClaimDropdownOpen(false) }} disabled={!hasPermissions?.includes("add_subrogationclaims") || (!incidentLoading && !incidentData?.incident?.incidentDate)}>
                                    Add Subrogation Claim
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                )}
            </div>

            {isLoading ? (
                <Skeleton count={5} height={64} />
            ) : (
                <div className="claims-sections">
                    {liabilityClaims.length > 0 && (
                        <div className="claims-section">
                            <div className="claims-section__title">Liability Claims ({liabilityClaims.length})</div>
                            <div className="claims-list">
                                {liabilityClaims.map((claim) => renderClaimRow({ claim, claimType: 'liability' }))}
                            </div>
                        </div>
                    )}
                    {claims.length > 0 && (
                        <div className="claims-section">
                            <div className="claims-section__title">Subrogation Claims ({claims.length})</div>
                            <div className="claims-list">
                                {claims.map((claim) => renderClaimRow({ claim, claimType: 'subrogation' }))}
                            </div>
                        </div>
                    )}
                    {totalClaims === 0 && <div className="claims-empty">No claims added.</div>}
                </div>
            )}
        </div>
    )
}

export default withApolloClient(ClaimsTab)
